/**
 * The family invitation link (row 3.7): myfinalkey.com/accept-invite/family
 * ?token=… opens here in the app (universal link) and on the web (the site
 * forwards to app.myfinalkey.com). One screen for both surfaces.
 *
 * Signed in: shows who invited and to which plan, and Accept calls
 * POST /family/accept. Signed out: sign-up or sign-in with the invited
 * address joins the plan by itself (AuthService, at registration and at
 * every password sign-in), so the screen only points there.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  SafeAreaView,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';

import { API_URL } from '../../config';
import { getAuthToken } from '../../services/auth/tokenStorage';
import { sessionManager } from '../../services/sessionManager';
import { createLogger } from '../../utils/logger';

const logger = createLogger('FamilyInvite');

type Preview = {
  ownerName: string;
  planType: string;
  role: string;
  invitedEmail: string;
  state: 'pending' | 'accepted' | 'inactive';
};

type Phase =
  | { kind: 'loading' }
  | { kind: 'invalid' }
  | { kind: 'ready'; preview: Preview; signedIn: boolean }
  | { kind: 'accepting'; preview: Preview }
  | { kind: 'joined'; ownerName: string }
  | { kind: 'problem'; preview: Preview; message: string };

const PLAN_NAMES: Record<string, string> = {
  family_bundle: 'Family Bundle',
  premium_spouse: 'Premium for two',
  plus_spouse: 'Plus for two',
  basic_spouse: 'Basic for two',
};

export function planName(planType: string): string {
  return PLAN_NAMES[planType] ?? 'family plan';
}

/** A session that is still good, refreshed if it was about to lapse. */
async function hasUsableSession(): Promise<boolean> {
  const token = await getAuthToken();
  if (!token) return false;
  if (await sessionManager.isTokenExpiredOrExpiring()) {
    await sessionManager.tryRefreshToken();
    if (await sessionManager.isTokenExpiredOrExpiring()) return false;
  }
  return true;
}

export const FamilyInviteScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const route = useRoute<any>();
  const token: string | undefined = route.params?.token;
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });

  const load = useCallback(async () => {
    if (!token) {
      setPhase({ kind: 'invalid' });
      return;
    }
    try {
      const res = await fetch(`${API_URL}/family/invitation?token=${encodeURIComponent(token)}`);
      if (!res.ok) {
        setPhase({ kind: 'invalid' });
        return;
      }
      const body = await res.json();
      setPhase({ kind: 'ready', preview: body.data as Preview, signedIn: await hasUsableSession() });
    } catch (error) {
      logger.error('Could not load the invitation:', error);
      setPhase({ kind: 'invalid' });
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const accept = async (preview: Preview) => {
    setPhase({ kind: 'accepting', preview });
    try {
      const authToken = await getAuthToken();
      const res = await fetch(`${API_URL}/family/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ token }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok) {
        setPhase({ kind: 'joined', ownerName: body.ownerName || preview.ownerName });
        return;
      }
      if (res.status === 404 && (await alreadyInFamily())) {
        // Signing in already joined the plan (AuthService auto-accept).
        setPhase({ kind: 'joined', ownerName: preview.ownerName });
        return;
      }
      const message =
        res.status === 403
          ? `This invitation was sent to ${preview.invitedEmail}, and you're signed in with a different address. Sign out, then sign in or create an account with ${preview.invitedEmail}.`
          : typeof body.message === 'string'
            ? body.message
            : 'The invitation could not be accepted. Try again.';
      setPhase({ kind: 'problem', preview, message });
    } catch (error) {
      logger.error('Accept failed:', error);
      setPhase({ kind: 'problem', preview, message: 'Check your connection and try again.' });
    }
  };

  const alreadyInFamily = async (): Promise<boolean> => {
    try {
      const res = await fetch(`${API_URL}/family/status`, {
        headers: { Authorization: `Bearer ${await getAuthToken()}` },
      });
      const status = await res.json();
      return !!status?.isInFamily;
    } catch {
      return false;
    }
  };

  const goToAuth = (screen: 'Login' | 'SignUp') =>
    navigation.reset({ index: 0, routes: [{ name: 'Auth', state: { routes: [{ name: screen }] } }] });

  const goHome = () => navigation.reset({ index: 0, routes: [{ name: 'Dashboard' }] });

  const goToFamilyPlan = () =>
    navigation.reset({
      index: 1,
      routes: [{ name: 'Dashboard' }, { name: 'Profile', state: { routes: [{ name: 'FamilyPlan' }] } }],
    });

  const body = (() => {
    switch (phase.kind) {
      case 'loading':
        return <ActivityIndicator color="#29BFC0" style={{ marginVertical: 40 }} />;
      case 'invalid':
        return (
          <>
            <Text style={styles.title}>This invitation link doesn't work</Text>
            <Text style={styles.text}>
              It may have been replaced by a newer invitation or withdrawn. Ask the person who
              invited you to send it again from their Family Plan screen.
            </Text>
            <Primary label="Go to FinalKey" onPress={() => goToAuth('Login')} />
          </>
        );
      case 'joined':
        return (
          <>
            <Text style={styles.title}>You're on the family plan</Text>
            <Text style={styles.text}>
              {phase.ownerName} covers your FinalKey subscription. Your vault stays your own: nobody
              on the plan can see what you keep in it.
            </Text>
            <Primary label="See your family plan" onPress={goToFamilyPlan} />
            <Secondary label="Go to your dashboard" onPress={goHome} />
          </>
        );
      case 'accepting':
      case 'ready':
      case 'problem': {
        const p = phase.preview;
        const heading = (
          <>
            <Text style={styles.title}>
              {p.ownerName} invited you to their {planName(p.planType)}
            </Text>
            <Text style={styles.text}>
              {p.ownerName} covers your FinalKey subscription as their {p.role === 'child' ? 'child' : 'partner'}.
              Your vault stays your own: nobody on the plan can see what you keep in it.
            </Text>
            <Text style={styles.meta}>Sent to {p.invitedEmail}</Text>
          </>
        );
        if (p.state === 'inactive') {
          return (
            <>
              {heading}
              <Text style={styles.notice}>This family plan has ended, so the invitation can't be accepted.</Text>
              <Primary label="Go to FinalKey" onPress={() => goToAuth('Login')} />
            </>
          );
        }
        if (p.state === 'accepted') {
          return (
            <>
              {heading}
              <Text style={styles.notice}>This invitation has already been accepted.</Text>
              {phase.kind === 'ready' && phase.signedIn ? (
                <Primary label="See your family plan" onPress={goToFamilyPlan} />
              ) : (
                <Primary label="Sign in" onPress={() => goToAuth('Login')} />
              )}
            </>
          );
        }
        if (phase.kind === 'ready' && !phase.signedIn) {
          return (
            <>
              {heading}
              <Text style={styles.text}>
                Create your account or sign in with {p.invitedEmail}, and you join the plan
                automatically.
              </Text>
              <Primary label="Create an account" onPress={() => goToAuth('SignUp')} />
              <Secondary label="I already have an account" onPress={() => goToAuth('Login')} />
            </>
          );
        }
        return (
          <>
            {heading}
            {phase.kind === 'problem' && <Text style={styles.notice}>{phase.message}</Text>}
            <Primary
              label={phase.kind === 'accepting' ? 'Joining…' : 'Accept the invitation'}
              disabled={phase.kind === 'accepting'}
              onPress={() => accept(p)}
            />
            <Secondary label="Not now" onPress={goHome} />
          </>
        );
      }
    }
  })();

  return (
    <SafeAreaView style={styles.safeArea}>
      <ScrollView contentContainerStyle={styles.content}>
        <Text style={styles.brand}>FinalKey</Text>
        <View style={styles.card}>{body}</View>
      </ScrollView>
    </SafeAreaView>
  );
};

const Primary: React.FC<{ label: string; onPress: () => void; disabled?: boolean }> = ({ label, onPress, disabled }) => (
  <TouchableOpacity
    style={[styles.primary, disabled && { opacity: 0.6 }]}
    onPress={onPress}
    disabled={disabled}
    accessibilityRole="button"
  >
    <Text style={styles.primaryText}>{label}</Text>
  </TouchableOpacity>
);

const Secondary: React.FC<{ label: string; onPress: () => void }> = ({ label, onPress }) => (
  <TouchableOpacity style={styles.secondary} onPress={onPress} accessibilityRole="button">
    <Text style={styles.secondaryText}>{label}</Text>
  </TouchableOpacity>
);

const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: '#29BFC0' },
  content: { flexGrow: 1, justifyContent: 'center', padding: 20 },
  brand: { fontSize: 28, fontWeight: '700', color: '#FFFFFF', textAlign: 'center', marginBottom: 20 },
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 20,
    padding: 24,
    width: '100%',
    maxWidth: 520,
    alignSelf: 'center',
    gap: 12,
  },
  title: { fontSize: 21, fontWeight: '700', color: '#1C2C4F' },
  text: { fontSize: 15, lineHeight: 22, color: '#4B5563' },
  meta: { fontSize: 13, color: '#6B7280' },
  notice: { fontSize: 14, lineHeight: 20, color: '#7A4A12', backgroundColor: '#FFF6EC', padding: 12, borderRadius: 10 },
  primary: { marginTop: 8, backgroundColor: '#29BFC0', borderRadius: 26, paddingVertical: 14, alignItems: 'center' },
  primaryText: { color: '#FFFFFF', fontSize: 16, fontWeight: '700' },
  secondary: { paddingVertical: 10, alignItems: 'center' },
  secondaryText: { color: '#1C2C4F', fontSize: 15, fontWeight: '600' },
});

export default FamilyInviteScreen;
